import { apiFetch } from '@/lib/api';
import * as Sentry from '@sentry/react';
import { isNative } from '@/lib/capacitor';
import { router } from '@inertiajs/react';
import { classifyActionUrl, navigateToActionUrl } from '@/lib/action-url';
import { toast } from '@/lib/haptic-toast';
import { hapticLight } from '@/lib/haptics';
import { trackEvent } from '@/lib/analytics';
import { journalPushTiming } from '@/lib/journal-notification-timing';
import i18n from 'i18next';

let initialized = false;

export async function initPushNotifications(routes) {
  if (!isNative() || initialized) return;
  initialized = true;

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    const permResult = await PushNotifications.requestPermissions();
    // Push opt-in is an onboarding sub-funnel: measure grant vs. deny so the
    // effect of prompt timing/copy on opt-in rate is visible.
    trackEvent('push_permission', {
      granted: permResult.receive === 'granted',
    });
    if (permResult.receive !== 'granted') return;

    let registered = false;
    let retryRegistration = false;
    let registrationRetries = 0;
    const clearDelivered = async () => {
      if (!registered) return;
      try {
        await PushNotifications.removeAllDeliveredNotifications();
      } catch (error) {
        Sentry.addBreadcrumb({
          category: 'push',
          message: 'Could not clear delivered notifications',
          level: 'warning',
          data: { error: String(error) },
        });
      }
    };

    // register() resolves before APNs delivers its token. The iOS plugin rejects
    // notification clearing until that callback; install listeners first, and
    // clear only once registration actually succeeds (FRONTEND-45 / -4M).
    await PushNotifications.addListener('registration', async (token) => {
      registered = true;
      retryRegistration = false;
      await clearDelivered();
      const platform = detectPlatform();
      await registerTokenWithRetry(routes.pushTokens, token.value, platform);
    });

    await PushNotifications.addListener('registrationError', (err) => {
      const message = err?.error ?? JSON.stringify(err);
      // FCM's SERVICE_NOT_AVAILABLE is a recoverable upstream/network failure,
      // not a broken app configuration (FRONTEND-4X). Retry on foreground return.
      if (/\bSERVICE_NOT_AVAILABLE\b/.test(message)) {
        registered = false;
        retryRegistration = true;
        Sentry.addBreadcrumb({
          category: 'push',
          message: 'FCM temporarily unavailable; registration pending',
          level: 'warning',
        });
        return;
      }
      Sentry.captureException(
        new Error(`Push registration failed: ${message}`),
        {
          tags: { platform: detectPlatform(), area: 'push_registration' },
        },
      );
    });

    // Create Android notification channels (maps to FCM channel_id)
    if (detectPlatform() === 'android') {
      const channels = [
        {
          id: 'social',
          name: i18n.t('notifications.categories.social'),
          importance: 3,
        },
        {
          id: 'reminder',
          name: i18n.t('notifications.categories.reminder'),
          importance: 3,
        },
        {
          id: 'achievement',
          name: i18n.t('notifications.categories.achievement'),
          importance: 2,
        },
        {
          id: 'security',
          name: i18n.t('notifications.categories.security'),
          importance: 4,
        },
        {
          id: 'transactional',
          name: i18n.t('notifications.categories.transactional'),
          importance: 3,
        },
      ];
      for (const ch of channels) {
        PushNotifications.createChannel(ch).catch(() => {});
      }
    }

    PushNotifications.addListener(
      'pushNotificationReceived',
      (notification) => {
        hapticLight();
        const title = notification.title || '';
        const body = notification.body || '';
        const actionUrl = notification.data?.action_url;
        const hasTarget = classifyActionUrl(actionUrl).kind !== 'none';

        toast(title, {
          description: body,
          action: hasTarget
            ? {
                label: i18n.t('common.view'),
                onClick: () => openPushNotification(notification),
              }
            : undefined,
        });
      },
    );

    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action) => {
        openPushNotification(action.notification);
      },
    );

    // Clear delivered notifications when the app resumes from a real
    // background. Use 'resume' (not appStateChange isActive:true / iOS
    // didBecomeActive, which also fires on transient interruptions like
    // Control Center or a permission sheet) so this only runs on a genuine
    // foreground return — consistent with the session-validation listener in
    // @/lib/capacitor.
    const { App } = await import('@capacitor/app');
    await App.addListener('resume', async () => {
      await clearDelivered();
      if (retryRegistration && registrationRetries < 2) {
        retryRegistration = false;
        registrationRetries += 1;
        try {
          await PushNotifications.register();
        } catch (error) {
          Sentry.addBreadcrumb({
            category: 'push',
            message: 'Push registration retry failed',
            level: 'warning',
            data: { error: String(error) },
          });
        }
      }
    });
    await PushNotifications.register();
  } catch (e) {
    Sentry.addBreadcrumb({
      category: 'push',
      message: 'Failed to initialize push notifications',
      level: 'warning',
      data: { error: String(e) },
    });
  }
}

export function detectPlatform() {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('iphone') || ua.includes('ipad')) return 'ios';
  if (ua.includes('android')) return 'android';
  return 'web';
}

async function registerTokenWithRetry(url, tokenValue, platform, retries = 3) {
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const res = await apiFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken(),
        },
        body: JSON.stringify({
          token: tokenValue,
          platform,
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });
      if (res.ok) return;
    } catch {
      // Network error — retry
    }
    await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt));
  }
  Sentry.addBreadcrumb({
    category: 'push',
    message: 'Failed to register token after retries',
    level: 'warning',
  });
}

function getCSRFToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || '';
}

function openPushNotification(notification) {
  const type = notification?.data?.notification_type || 'unknown';
  // Firebase reserves notification_open; retain the established custom name.
  trackEvent('push_opened', {
    category: notification?.data?.channel_id || 'unknown',
    notification_type: type,
    ...journalPushTiming(type),
  });
  navigateToActionUrl(notification?.data?.action_url, { router });
}
